import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, TextField, MenuItem, Button, Chip, IconButton, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Dialog, DialogTitle, DialogContent, DialogActions, Grid, Tooltip, Divider, Alert,
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, FileDownload as DownloadIcon, Print as PrintIcon,
  CheckCircle as CheckIcon, AutoFixHigh as GenerateIcon, Groups as PayrollIcon, DoneAll as DoneAllIcon,
} from '@mui/icons-material';
import * as XLSX from 'xlsx';
import { accountingAPI, teacherAPI } from '../../services/api';
import { money, money2, label, errorText } from '../../utils/format';
import { fmtDate, localDate, MONTH_NAMES } from '../../utils/dates';
import { printPage, esc } from '../../utils/print';
import { PAYMENT_MODES, amountInWords, MiniStat } from './common';

const DEPARTMENTS = ['TEACHING', 'ADMINISTRATION', 'LIBRARY', 'LAB', 'HOSTEL', 'TRANSPORT', 'MAINTENANCE', 'OTHER'];
const emptyForm = (m, y) => ({
  teacherId: '', staffName: '', designation: '', department: 'TEACHING', month: m, year: y,
  basicSalary: '', hra: '', da: '', otherAllowances: '', deductions: '', paymentMode: 'BANK_TRANSFER', transactionId: '', remarks: '', status: 'PENDING',
});
const num = (v) => parseFloat(v || 0) || 0;

export default function PayrollTab({ notify, onChanged }) {
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [all, setAll] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm(month, year));
  const [confirm, setConfirm] = useState(null);
  const [bulk, setBulk] = useState(null); // 'generate' | 'paid'
  const [bulkMode, setBulkMode] = useState('BANK_TRANSFER');

  const load = useCallback(async () => {
    try {
      setAll((await accountingAPI.getSalaries()).data);
      try { setTeachers((await teacherAPI.getAll()).data); } catch (e) { setTeachers([]); }
    } catch (e) { notify(errorText(e, 'Could not load payroll'), 'error'); }
  }, [notify]);
  useEffect(() => { load(); }, [load]);

  const rows = all.filter((s) => s.month === month && s.year === year).sort((a, b) => a.staffName.localeCompare(b.staffName));
  const gross = rows.reduce((s, r) => s + num(r.grossSalary), 0);
  const deductions = rows.reduce((s, r) => s + num(r.deductions), 0);
  const net = rows.reduce((s, r) => s + num(r.netSalary), 0);
  const paid = rows.filter((r) => r.status === 'PAID').reduce((s, r) => s + num(r.netSalary), 0);
  const pendingCount = rows.filter((r) => r.status !== 'PAID').length;

  const grossOf = (f) => num(f.basicSalary) + num(f.hra) + num(f.da) + num(f.otherAllowances);

  const openDialog = (p = null) => {
    setEditing(p);
    setForm(p ? {
      teacherId: p.teacher?.id || '', staffName: p.staffName || '', designation: p.designation || '', department: p.department || 'TEACHING', month: p.month, year: p.year,
      basicSalary: p.basicSalary ?? '', hra: p.hra ?? '', da: p.da ?? '', otherAllowances: p.otherAllowances ?? '', deductions: p.deductions ?? '',
      paymentMode: p.paymentMode || 'BANK_TRANSFER', transactionId: p.transactionId || '', remarks: p.remarks || '', status: p.status || 'PENDING',
    } : emptyForm(month, year));
    setOpen(true);
  };

  const pickTeacher = (id) => {
    const t = teachers.find((x) => String(x.id) === String(id));
    setForm((f) => ({ ...f, teacherId: id, staffName: t ? `${t.firstName} ${t.lastName}` : f.staffName, designation: t?.designation || f.designation, department: t ? 'TEACHING' : f.department }));
  };

  const save = async () => {
    if (!form.staffName || form.basicSalary === '') { notify('Staff name and basic salary are required', 'error'); return; }
    try {
      const data = {
        ...form, teacher: form.teacherId ? { id: parseInt(form.teacherId, 10) } : null,
        basicSalary: num(form.basicSalary), hra: num(form.hra), da: num(form.da), otherAllowances: num(form.otherAllowances), deductions: num(form.deductions),
      };
      delete data.teacherId;
      if (editing) await accountingAPI.updateSalary(editing.id, data); else await accountingAPI.createSalary(data);
      notify(editing ? 'Salary record updated' : 'Salary record added');
      setOpen(false);
      load();
      onChanged();
    } catch (e) { notify(errorText(e, 'Could not save the salary record'), 'error'); }
  };

  const markPaid = async (p) => {
    try {
      await accountingAPI.updateSalary(p.id, { ...p, teacher: p.teacher ? { id: p.teacher.id } : null, status: 'PAID', paymentDate: localDate() });
      notify('Marked as paid');
      load();
      onChanged();
    } catch (e) { notify(errorText(e, 'Could not update'), 'error'); }
  };

  const remove = async () => {
    try { await accountingAPI.deleteSalary(confirm.id); notify('Salary record deleted'); setConfirm(null); load(); onChanged(); }
    catch (e) { notify(errorText(e, 'Could not delete'), 'error'); }
  };

  const runBulk = async () => {
    try {
      if (bulk === 'generate') {
        const r = await accountingAPI.generatePayroll({ month, year });
        notify(r.data.message);
      } else {
        const r = await accountingAPI.markAllPaid({ month, year, paymentMode: bulkMode });
        notify(r.data.message);
      }
      setBulk(null);
      load();
      onChanged();
    } catch (e) { notify(errorText(e, 'That did not work'), 'error'); setBulk(null); }
  };

  const exportExcel = () => {
    const sheet = rows.map((p) => ({
      'Staff name': p.staffName, Designation: p.designation || '', Department: label(p.department), Month: MONTH_NAMES[p.month - 1], Year: p.year,
      'Basic (₹)': num(p.basicSalary), 'HRA (₹)': num(p.hra), 'DA (₹)': num(p.da), 'Other allowances (₹)': num(p.otherAllowances),
      'Deductions (₹)': num(p.deductions), 'Gross (₹)': num(p.grossSalary), 'Net (₹)': num(p.netSalary), Status: p.status, 'Paid on': p.paymentDate || '', Mode: label(p.paymentMode),
    }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(sheet), 'Payroll');
    XLSX.writeFile(wb, `payroll_${MONTH_NAMES[month - 1]}_${year}.xlsx`);
  };

  const printRegister = () => {
    const body = `<div class="grid">
        <div class="card"><div class="v">${money(gross)}</div><div class="l">Gross</div></div>
        <div class="card"><div class="v red">${money(deductions)}</div><div class="l">Deductions</div></div>
        <div class="card"><div class="v green">${money(net)}</div><div class="l">Net payable</div></div>
        <div class="card"><div class="v">${rows.length}</div><div class="l">Staff</div></div></div>
      <table><thead><tr><th>Name</th><th>Designation</th><th class="r">Basic</th><th class="r">HRA</th><th class="r">DA</th><th class="r">Other</th><th class="r">Deductions</th><th class="r">Net</th><th>Status</th></tr></thead><tbody>
      ${rows.map((p) => `<tr><td>${esc(p.staffName)}</td><td>${esc(p.designation)}</td><td class="r">${money(p.basicSalary)}</td><td class="r">${money(p.hra)}</td><td class="r">${money(p.da)}</td><td class="r">${money(p.otherAllowances)}</td><td class="r">${money(p.deductions)}</td><td class="r b">${money(p.netSalary)}</td><td>${esc(p.status)}</td></tr>`).join('')}
      <tr><td colspan="7" class="r b">Total net payable</td><td class="r b">${money(net)}</td><td></td></tr></tbody></table>
      <div class="sig"><div>Prepared by</div><div>Accountant</div><div>Principal</div></div>`;
    printPage('Payroll Register', `${MONTH_NAMES[month - 1]} ${year}`, body);
  };

  const printPayslip = (p) => {
    const earn = [['Basic salary', p.basicSalary], ['House rent allowance (HRA)', p.hra], ['Dearness allowance (DA)', p.da], ['Other allowances', p.otherAllowances]];
    const body = `<div style="max-width:720px;margin:6px auto">
      <table style="margin-bottom:14px"><tbody>
        <tr><td style="width:22%"><b>Name</b></td><td style="width:28%">${esc(p.staffName)}</td><td style="width:22%"><b>Designation</b></td><td>${esc(p.designation || '-')}</td></tr>
        <tr><td><b>Department</b></td><td>${esc(label(p.department))}</td><td><b>Pay period</b></td><td>${esc(MONTH_NAMES[p.month - 1])} ${p.year}</td></tr>
        <tr><td><b>Payment mode</b></td><td>${esc(label(p.paymentMode))}</td><td><b>Status</b></td><td>${esc(p.status)}${p.paymentDate ? ' on ' + esc(fmtDate(p.paymentDate)) : ''}</td></tr>
      </tbody></table>
      <table><thead><tr><th>Earnings</th><th class="r">Amount</th><th>Deductions</th><th class="r">Amount</th></tr></thead><tbody>
        ${earn.map((e, i) => `<tr><td>${e[0]}</td><td class="r">${money2(e[1])}</td><td>${i === 0 ? 'Provident fund and other deductions' : ''}</td><td class="r">${i === 0 ? money2(p.deductions) : ''}</td></tr>`).join('')}
        <tr><td class="b">Gross earnings</td><td class="r b">${money2(p.grossSalary)}</td><td class="b">Total deductions</td><td class="r b">${money2(p.deductions)}</td></tr>
      </tbody></table>
      <div style="margin-top:14px;padding:12px 14px;background:#e0f2f1;border-radius:8px;display:flex;justify-content:space-between;font-size:16px"><b>Net pay</b><b>${money2(p.netSalary)}</b></div>
      <p style="margin-top:8px;font-size:12px">Rupees in words: <b>${esc(amountInWords(p.netSalary))}</b></p>
      <div class="sig"><div>Employee signature</div><div>Accountant</div><div>Principal</div></div></div>`;
    printPage('Salary Slip', `${p.staffName} - ${MONTH_NAMES[p.month - 1]} ${p.year}`, body);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField select size="small" label="Month" value={month} onChange={(e) => setMonth(e.target.value)} sx={{ minWidth: 150 }}>
            {MONTH_NAMES.map((m, i) => <MenuItem key={m} value={i + 1}>{m}</MenuItem>)}
          </TextField>
          <TextField size="small" type="number" label="Year" value={year} onChange={(e) => setYear(parseInt(e.target.value, 10) || now.getFullYear())} sx={{ width: 110 }} />
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button variant="outlined" size="small" startIcon={<GenerateIcon />} onClick={() => setBulk('generate')}>Generate from last month</Button>
          <Button variant="outlined" size="small" color="success" startIcon={<DoneAllIcon />} onClick={() => setBulk('paid')} disabled={!pendingCount}>Mark all paid</Button>
          <Button variant="outlined" size="small" startIcon={<PrintIcon />} onClick={printRegister} disabled={!rows.length}>Register</Button>
          <Button variant="outlined" size="small" startIcon={<DownloadIcon />} onClick={exportExcel} disabled={!rows.length}>Excel</Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => openDialog()}>Add salary</Button>
        </Box>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(5, 1fr)' }, gap: 2, mb: 2 }}>
        <MiniStat label="Gross" value={money(gross)} />
        <MiniStat label="Deductions" value={money(deductions)} color="#c62828" bg="#ffebee" />
        <MiniStat label="Net payable" value={money(net)} color="#00796b" bg="#e8f5e9" />
        <MiniStat label="Paid" value={money(paid)} color="#2e7d32" bg="#e8f5e9" />
        <MiniStat label="Pending" value={money(net - paid)} color="#e65100" bg="#fff3e0" />
      </Box>

      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer>
          <Table size="small">
            <TableHead><TableRow>
              <TableCell>Staff</TableCell><TableCell>Designation</TableCell><TableCell>Department</TableCell>
              <TableCell align="right">Gross</TableCell><TableCell align="right">Deductions</TableCell><TableCell align="right">Net salary</TableCell>
              <TableCell align="center">Status</TableCell><TableCell align="center">Actions</TableCell>
            </TableRow></TableHead>
            <TableBody>
              {rows.map((p) => (
                <TableRow key={p.id} hover>
                  <TableCell sx={{ fontWeight: 600 }}>{p.staffName}</TableCell>
                  <TableCell>{p.designation || '-'}</TableCell>
                  <TableCell>{label(p.department)}</TableCell>
                  <TableCell align="right">{money(p.grossSalary)}</TableCell>
                  <TableCell align="right" sx={{ color: '#c62828' }}>{money(p.deductions)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>{money(p.netSalary)}</TableCell>
                  <TableCell align="center">
                    <Chip size="small" label={p.status} sx={p.status === 'PAID' ? { bgcolor: '#c8e6c9', color: '#1b5e20', fontWeight: 700 } : { bgcolor: '#ffe0b2', color: '#e65100', fontWeight: 700 }} />
                  </TableCell>
                  <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                    <Tooltip title="Print salary slip"><IconButton size="small" onClick={() => printPayslip(p)}><PrintIcon fontSize="small" /></IconButton></Tooltip>
                    {p.status !== 'PAID' && <Tooltip title="Mark as paid"><IconButton size="small" color="success" onClick={() => markPaid(p)}><CheckIcon fontSize="small" /></IconButton></Tooltip>}
                    <IconButton size="small" onClick={() => openDialog(p)}><EditIcon fontSize="small" /></IconButton>
                    <IconButton size="small" color="error" onClick={() => setConfirm(p)}><DeleteIcon fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && (
                <TableRow><TableCell colSpan={8} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                  No salary records for {MONTH_NAMES[month - 1]} {year}. Use "Generate from last month" to create them in one click.
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Add / edit */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#004d40', color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}><PayrollIcon /> {editing ? 'Edit salary record' : 'Add salary record'}</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth size="small" label="Link to faculty (optional)" value={form.teacherId} onChange={(e) => pickTeacher(e.target.value)}>
                <MenuItem value="">Not linked (other staff)</MenuItem>
                {teachers.map((t) => <MenuItem key={t.id} value={t.id}>{t.firstName} {t.lastName}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Staff name" value={form.staffName} onChange={(e) => setForm({ ...form, staffName: e.target.value })} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Designation" value={form.designation} onChange={(e) => setForm({ ...form, designation: e.target.value })} /></Grid>
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth size="small" label="Department" value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })}>
                {DEPARTMENTS.map((d) => <MenuItem key={d} value={d}>{label(d)}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField select fullWidth size="small" label="Month" value={form.month} onChange={(e) => setForm({ ...form, month: e.target.value })}>
                {MONTH_NAMES.map((m, i) => <MenuItem key={m} value={i + 1}>{m}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3}><TextField fullWidth size="small" type="number" label="Year" value={form.year} onChange={(e) => setForm({ ...form, year: parseInt(e.target.value, 10) })} /></Grid>
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth size="small" label="Status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                <MenuItem value="PENDING">Pending</MenuItem><MenuItem value="PAID">Paid</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}><Divider>Salary components</Divider></Grid>
            {[['basicSalary', 'Basic (₹)'], ['hra', 'HRA (₹)'], ['da', 'DA (₹)'], ['otherAllowances', 'Other (₹)']].map(([k, l]) => (
              <Grid item xs={6} sm={3} key={k}><TextField fullWidth size="small" type="number" label={l} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} /></Grid>
            ))}
            <Grid item xs={6} sm={4}><TextField fullWidth size="small" type="number" label="Deductions (₹)" value={form.deductions} onChange={(e) => setForm({ ...form, deductions: e.target.value })} /></Grid>
            <Grid item xs={6} sm={4}>
              <TextField select fullWidth size="small" label="Payment mode" value={form.paymentMode} onChange={(e) => setForm({ ...form, paymentMode: e.target.value })}>
                {PAYMENT_MODES.map((m) => <MenuItem key={m} value={m}>{label(m)}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth size="small" label="Transaction id" value={form.transactionId} onChange={(e) => setForm({ ...form, transactionId: e.target.value })} /></Grid>
            <Grid item xs={12}><TextField fullWidth size="small" label="Remarks" value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} /></Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', bgcolor: '#e0f2f1', p: 1.5, borderRadius: 2 }}>
                <Typography fontWeight={700} color="#004d40">Gross: {money(grossOf(form))}</Typography>
                <Typography fontWeight={800} color="#004d40">Net payable: {money(grossOf(form) - num(form.deductions))}</Typography>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={save}>{editing ? 'Update' : 'Save'}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!confirm} onClose={() => setConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete salary record?</DialogTitle>
        <DialogContent><Typography>{confirm?.staffName} - {confirm ? MONTH_NAMES[confirm.month - 1] : ''} {confirm?.year}. This cannot be undone.</Typography></DialogContent>
        <DialogActions sx={{ p: 2.5 }}><Button onClick={() => setConfirm(null)}>Cancel</Button><Button color="error" variant="contained" onClick={remove}>Delete</Button></DialogActions>
      </Dialog>

      <Dialog open={!!bulk} onClose={() => setBulk(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{bulk === 'generate' ? 'Generate payroll' : 'Mark all as paid'}</DialogTitle>
        <DialogContent>
          {bulk === 'generate' ? (
            <Alert severity="info">Creates pending salary records for {MONTH_NAMES[month - 1]} {year} by copying last month's amounts. Staff who already have a record this month are skipped. You can edit any amount afterwards.</Alert>
          ) : (
            <>
              <Alert severity="warning" sx={{ mb: 2 }}>{pendingCount} pending salary record(s) for {MONTH_NAMES[month - 1]} {year} will be marked as paid today.</Alert>
              <TextField select fullWidth size="small" label="Payment mode" value={bulkMode} onChange={(e) => setBulkMode(e.target.value)}>
                {PAYMENT_MODES.map((m) => <MenuItem key={m} value={m}>{label(m)}</MenuItem>)}
              </TextField>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}><Button onClick={() => setBulk(null)}>Cancel</Button><Button variant="contained" onClick={runBulk}>{bulk === 'generate' ? 'Generate' : 'Mark as paid'}</Button></DialogActions>
      </Dialog>
    </Box>
  );
}
